import React from 'react';
import { Spin } from 'antd';
import { DegradationNote } from '~containers/analytics/kpis/degradation-cause';
import generatePalette from '~utils/generate-palette';
import { CHOICE_BAR_COLORS, GRAY_500 } from '~styles/colors';
import { labelColorOn } from '~styles/contrast';
import { buildMilestoneChoicePanels } from './milestone-choices';
import type {
    MilestoneChoiceOption,
    MilestoneChoicePanel,
} from './milestone-choices';
import type { CampaignTierLadderState } from './use-campaign-tier-ladder';
import type { CampaignMilestoneChoicesState } from './use-campaign-milestone-choices';
import style from './milestone-choice-bars.module.scss';

export interface MilestoneChoiceBarsProps {
    tierLadder: CampaignTierLadderState;
    choices: CampaignMilestoneChoicesState;
}

const AWAITING_LABEL = 'Awaiting choice';

/** Said once for the block rather than on each of the six bars: it is a
 * property of the bar's shape, not of any one milestone. */
const ONE_DENOMINATOR_NOTE =
    'A bar is everyone who reached the milestone: the coupons split the share who chose, Awaiting choice is the rest. A bar drawn short is a milestone whose choices could not be attributed.';

const formatCount = (value: number): string => value.toLocaleString();

const formatPct = (pct: number): string => `${Math.round(pct)}%`;

interface BarSegment {
    key: string;
    label: string;
    count: number;
    pct: number;
    color: string;
}

const byCountDesc = (
    a: MilestoneChoiceOption,
    b: MilestoneChoiceOption
): number => b.choices - a.choices || a.label.localeCompare(b.label);

const segmentsOf = (
    panel: MilestoneChoicePanel,
    breakdown: MilestoneChoiceOption[]
): BarSegment[] => {
    const sorted = [...breakdown].sort(byCountDesc);
    const ramp = generatePalette(CHOICE_BAR_COLORS, sorted.length);
    const coupons = sorted.map((option, index) => ({
        key: option.key,
        label: option.label,
        count: option.choices,
        pct: option.sharePct,
        color: ramp[index],
    }));
    return [
        ...coupons,
        {
            key: AWAITING_LABEL,
            label: AWAITING_LABEL,
            count: panel.awaitingMembers,
            pct: panel.awaitingPct,
            color: GRAY_500,
        },
    ];
};

/**
 * The stacked bar, drawn on ONE axis shared by every segment and every
 * milestone: a segment `pct` percent of the labels is `pct` percent of the
 * rail.
 *
 * Absolutely positioned rather than laid out in a flex row on purpose: flex
 * would rescale the segments to the rail's width in the drawing only, hiding
 * any disagreement between a width and the label sitting on it.
 */
const ChoiceBar: React.FC<{ segments: BarSegment[] }> = ({ segments }) => {
    let offset = 0;
    return (
        <div className={style.bar_scale}>
            {segments.map((segment) => {
                const left = offset;
                offset += segment.pct;
                return (
                    <div
                        key={segment.key}
                        className={style.bar_segment}
                        data-testid="choice-bar-segment"
                        title={`${segment.label} · ${formatPct(segment.pct)}`}
                        style={{
                            left: `${left}%`,
                            width: `${segment.pct}%`,
                            background: segment.color,
                        }}
                    >
                        {segment.pct >= 6 && (
                            <span
                                className={`${style.bar_segment_label} ${
                                    labelColorOn(segment.color) === 'light'
                                        ? style.bar_segment_label_light
                                        : style.bar_segment_label_dark
                                }`}
                            >
                                {formatPct(segment.pct)}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const Legend: React.FC<{ segments: BarSegment[] }> = ({ segments }) => (
    <ul className={style.legend}>
        {segments.map((segment) => (
            <li
                key={segment.key}
                className={style.legend_entry}
                data-testid="choice-legend-entry"
            >
                <span
                    className={style.legend_swatch}
                    style={{ background: segment.color }}
                />
                <span className={style.legend_label}>{segment.label}</span>
                <span className={style.legend_value}>
                    <strong>{formatCount(segment.count)}</strong>
                </span>
            </li>
        ))}
    </ul>
);

const MilestoneBar: React.FC<{
    panel: MilestoneChoicePanel;
    breakdown: MilestoneChoiceOption[];
}> = ({ panel, breakdown }) => {
    const segments = segmentsOf(panel, breakdown);
    return (
        <>
            <ChoiceBar segments={segments} />
            <Legend segments={segments} />
        </>
    );
};

const MilestonePanel: React.FC<{ panel: MilestoneChoicePanel }> = ({
    panel,
}) => {
    return (
        <section className={style.milestone_row} data-testid="milestone-panel">
            <div>
                <h4 className={style.milestone_title}>
                    Milestone {formatCount(panel.visits)}
                </h4>
                {panel.reachedMembers != null && panel.reachedMembers > 0 && (
                    <p className={style.milestone_reach}>
                        <strong>
                            {formatCount(
                                Math.min(
                                    panel.choosingMembers,
                                    panel.reachedMembers
                                )
                            )}
                        </strong>{' '}
                        of <strong>{formatCount(panel.reachedMembers)}</strong>{' '}
                        reached chose a coupon
                    </p>
                )}
            </div>

            <div>
                {panel.reachedMembers == null ? (
                    <p className={style.panel_empty}>
                        No figures available for this milestone.
                    </p>
                ) : panel.reachedMembers === 0 ? (
                    <p className={style.panel_empty}>
                        Nobody has reached this milestone yet.
                    </p>
                ) : (
                    <MilestoneBar
                        panel={panel}
                        breakdown={panel.choiceBreakdown}
                    />
                )}
            </div>
        </section>
    );
};

/**
 * One panel per milestone of the ladder, under the customer-group cards: its
 * cumulative threshold, how many of those who reached it chose a coupon, and
 * the stacked bar of that choice distribution.
 *
 * Every milestone is rendered, whatever its headcount. One nobody has reached
 * says so rather than drawing a bar at zero: three of the six were empty on
 * the campaign's fourth day of fifty-seven, so the empty half is the normal
 * early state and not a defect to hide.
 */
export const MilestoneChoiceBars: React.FC<MilestoneChoiceBarsProps> = ({
    tierLadder,
    choices,
}) => {
    if (tierLadder.status === 'loading' || choices.status === 'loading') {
        return <Spin />;
    }
    if (tierLadder.status === 'unavailable') {
        return <DegradationNote reason={tierLadder.reason} />;
    }
    if (choices.status === 'unavailable') {
        return (
            <DegradationNote reason={choices.reason} cause={choices.cause} />
        );
    }

    const panels = buildMilestoneChoicePanels(
        tierLadder.tierRewards,
        choices.reachByMilestone,
        choices.optionsByMilestone
    );

    if (panels.length === 0) {
        return (
            <p className={style.panel_empty}>
                No milestone offers a coupon choice.
            </p>
        );
    }

    return (
        <div className={style.panels}>
            <p className={style.denominators_note}>{ONE_DENOMINATOR_NOTE}</p>
            {panels.map((panel) => (
                <MilestonePanel key={panel.key} panel={panel} />
            ))}
        </div>
    );
};
